import React, {Dispatch, SetStateAction} from 'react'
import {useIntl} from 'react-intl'
import {Box, Flex, HStack, Text, useMultiStyleConfig, StackProps, ButtonProps, TextProps} from '@chakra-ui/react'
import {Button, Icon, HideOnDesktop, HideOnMobile} from '_uiComponents'

interface FilterButtonProps {
    isFilterListOpened: boolean
    selectedFilters?: number
    filterTotals?: number
    setIsFilterListOpened: Dispatch<SetStateAction<boolean>>
}

/**
 * @name FilterButton
 *
 * @component
 *
 * @description
 * Renders a button to toggle the filter list and display the number of selected filters.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isFilterListOpened - Indicates whether the filter list is opened.
 * @param {number} [props.selectedFilters] - The number of filters that have been selected.
 * @param {number} [props.filterTotals] - The total number of filters available.
 * @param {Dispatch<SetStateAction<boolean>>} props.setIsFilterListOpened - Function to set the state of filter list opened.
 *
 * @returns {JSX.Element} The rendered `FilterButton` component.
 */
const FilterButton: React.FC<FilterButtonProps> = ({isFilterListOpened, selectedFilters, filterTotals, setIsFilterListOpened}) => {
    const styles = useMultiStyleConfig('FilterButton') as Record<string, StackProps & ButtonProps & TextProps>
    const {formatMessage} = useIntl()

    const activeFiltersLabel = formatMessage({id: 'active.filter.number', defaultMessage: 'ACTIVE FILTERS ( {0} )'}, {0: selectedFilters})
    const openFiltersLabel = formatMessage({id: 'label.plp.filter.open', defaultMessage: 'SHOW FILTERS'})
    const closeFiltersLabel = formatMessage({id: 'label.plp.filter.close', defaultMessage: 'HIDE FILTERS'})
    const resultsLabel = formatMessage({id: 'search.results', defaultMessage: '{0} products found'}, {0: filterTotals})

    const hasActiveFilters = selectedFilters !== undefined && selectedFilters > 0

    return (
        <Box {...styles.filterButtonWrapper}>
            <HideOnDesktop>
                <Button
                    aria-label={openFiltersLabel}
                    variant="filter"
                    size="xs"
                    data-testid="filter-button"
                    onClick={() => setIsFilterListOpened(!isFilterListOpened)}
                >
                    <Text>{hasActiveFilters ? activeFiltersLabel : openFiltersLabel}</Text>
                    <Icon name="IconFilter" size="sm" />
                </Button>
            </HideOnDesktop>
            <HideOnMobile>
                <Flex {...styles.filterToggleWrapper}>
                    <HStack
                        as="button"
                        aria-label={openFiltersLabel}
                        aria-expanded={isFilterListOpened}
                        {...styles.filterTextLinkWrapper}
                        onClick={() => setIsFilterListOpened(!isFilterListOpened)}
                        data-testid="filter-text-link"
                    >
                        <Text {...styles.filterTriggerLabel}>
                            {hasActiveFilters ? activeFiltersLabel : !isFilterListOpened ? openFiltersLabel : closeFiltersLabel}
                        </Text>
                        <Box {...styles.filterIcon} {...(isFilterListOpened ? styles.filterIconOpened : {})}>
                            <Icon name="IconChevronRight" size="xs" />
                        </Box>
                    </HStack>
                    {hasActiveFilters && (
                        <Text {...styles.filterTotalLabel} role="status">
                            {resultsLabel}
                        </Text>
                    )}
                </Flex>
            </HideOnMobile>
        </Box>
    )
}

export {FilterButton}
